import React, { useMemo, useState } from 'react';
import { Trophy, CalendarClock, Info } from 'lucide-react';
import { formatCurrency } from '../utils/format';
import { CardBrandIcon } from './CardBrandIcon';

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const DAY_MS = 86400000;
const fmt = (d) => d.toLocaleDateString('pt-BR');

// Simula uma compra feita hoje: em qual fatura cai e quantos dias faltam para pagar.
// Mesma regra do servidor: comprou DEPOIS do dia de fechamento → entra na fatura do mês seguinte.
export function simularCompraHoje(cartao, agora = new Date()) {
  const fechaDia = Number(cartao.diaFechamento) || 1;
  const vencDia = Number(cartao.diaVencimento) || 1;
  const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  const mesFatura = hoje.getDate() > fechaDia ? 1 : 0;
  const fechamento = new Date(hoje.getFullYear(), hoje.getMonth() + mesFatura, fechaDia);
  const vencimento = new Date(hoje.getFullYear(), hoje.getMonth() + mesFatura + (vencDia > fechaDia ? 0 : 1), vencDia);
  return {
    fechamento,
    vencimento,
    dias: Math.round((vencimento - hoje) / DAY_MS),
    mesFatura: `${MESES[fechamento.getMonth()]} de ${fechamento.getFullYear()}`,
  };
}

export default function MelhorCartao({ cartoes }) {
  const [valorTxt, setValorTxt] = useState('');
  const valor = parseFloat(String(valorTxt).replace(/\./g, '').replace(',', '.')) || 0;

  const ranking = useMemo(() => {
    return cartoes
      .filter((c) => c.ativo !== false)
      .map((c) => {
        const sim = simularCompraHoje(c);
        const semLimite = valor > 0 && (c.limiteDisponivel || 0) < valor;
        return { cartao: c, ...sim, semLimite };
      })
      .sort((a, b) => (a.semLimite - b.semLimite) || (b.dias - a.dias) || ((b.cartao.limiteDisponivel || 0) - (a.cartao.limiteDisponivel || 0)));
  }, [cartoes, valor]);

  if (ranking.length === 0) return null;
  const melhor = ranking[0].semLimite ? null : ranking[0];

  return (
    <div className="fin-card p-4 sm:p-5 space-y-4" data-testid="melhor-cartao">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400 flex items-center justify-center"><Trophy size={18} /></span>
          <div>
            <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">Melhor cartão para comprar hoje</h2>
            <p className="text-[11px] text-slate-400">Quanto mais dias até o vencimento, mais tempo você tem para pagar</p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          Valor da compra (opcional)
          <input
            value={valorTxt}
            onChange={(e) => setValorTxt(e.target.value)}
            inputMode="decimal"
            placeholder="0,00"
            className="fin-input !w-28 !py-1.5 text-sm"
            data-testid="melhor-cartao-valor"
          />
        </label>
      </div>

      {melhor ? (
        <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="melhor-cartao-resumo">
          Use o <strong className="text-slate-900 dark:text-white">{melhor.cartao.nome}</strong>: uma compra de hoje entra na fatura de {melhor.mesFatura} e só vence em <strong>{fmt(melhor.vencimento)}</strong> ({melhor.dias} {melhor.dias === 1 ? 'dia' : 'dias'} para pagar).
        </p>
      ) : (
        <p className="text-sm text-rose-500 flex items-center gap-2"><Info size={15} />Nenhum cartão tem limite disponível para esse valor.</p>
      )}

      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {ranking.map((r, i) => {
          const eMelhor = melhor && i === 0;
          return (
            <li
              key={r.cartao.id}
              className={`p-3 rounded-2xl border flex items-center gap-3 ${eMelhor ? 'border-amber-300 bg-amber-50/60 dark:border-amber-400/40 dark:bg-amber-500/10' : 'border-slate-200/70 dark:border-white/10'} ${r.semLimite ? 'opacity-60' : ''}`}
              data-testid={`melhor-cartao-item-${i}`}
            >
              <CardBrandIcon bandeira={r.cartao.bandeira} width={34} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-bold text-slate-800 dark:text-slate-100 truncate">{r.cartao.nome}</span>
                  {eMelhor && <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-amber-500 text-white">Melhor hoje</span>}
                  {r.semLimite && <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-rose-500 text-white">Sem limite</span>}
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1 flex-wrap">
                  <CalendarClock size={11} />Fatura de {r.mesFatura} · vence {fmt(r.vencimento)} · <strong>{r.dias} dias</strong>
                </p>
                <p className="text-[11px] text-slate-400">Limite disponível {formatCurrency(r.cartao.limiteDisponivel || 0)} · fecha dia {r.cartao.diaFechamento}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
